import React from "react";
import dayjs from "dayjs";
import { Drawer } from "../../../ui/primitives/Drawer";
import { Button } from "../../../ui/primitives/Button";
import { Stack } from "../../../ui/primitives/Stack";
import { Tag } from "../../../ui/primitives/Tag";
import { Text } from "../../../ui/primitives/Text";
import { Input } from "../../../ui/primitives/Input";
import { DatePicker } from "../../../ui/primitives/DatePicker";
import { useTokens } from "../../../core/theme/TokensProvider";
import { useClientConfig } from "../../../common/client/ClientConfigProvider";
import { DEFAULT_SIGNUP_HOW_THEY_HEARD } from "../../../model/client/ClientSettings";
import { nextDueDate } from "../../../utils/memberFollowupUtils";
import { toDateOnly } from "../../../utils/DateOnly";
import { CallOutcome } from "../../../model/members/Calls";
import {
  LeadRow,
  leadSourceLabel,
  leadTypeLabel,
  howTheyHeardLabel,
  interestsLabel,
  telHref,
} from "./leadDisplay";

export interface LeadDetailPanelProps {
  /** The lead, including the manager's instruction from the batch it was assigned with. */
  row: LeadRow | null;
  noteDraft: string;
  onNoteDraftChange: (value: string) => void;
  onSaveNote: () => void;
  savingNote?: boolean;
  onEditFollowup: () => void;
  onGoToProfile: () => void;
  onClose: () => void;
  /**
   * FORT-361: the id of MY open call on this lead, or null when I hold none. Non-null is
   * what makes the quick outcome buttons appear — logging an outcome is only meaningful
   * against a call you actually owe.
   */
  openCallTaskId?: number | null;
  onRecordOutcome?: (
    taskId: number,
    outcome: CallOutcome,
    note: string,
    nextFollowUpDate: string
  ) => void;
  /** Which outcome is mid-flight, so only the button that was pressed shows a spinner. */
  pendingOutcome?: CallOutcome | null;
}

const OUTCOME_BUTTONS: { outcome: CallOutcome; label: string; testId: string }[] = [
  { outcome: "Reached", label: "Reached", testId: "lead-detail-outcome-reached" },
  { outcome: "NoAnswer", label: "No answer", testId: "lead-detail-outcome-noanswer" },
  { outcome: "LeftVoicemail", label: "Left VM", testId: "lead-detail-outcome-leftvm" },
];

// Takes a Date as well as a string: DateReviver revives ISO-looking response fields, so a
// follow-up date arrives here as either depending on the path it took.
const formatDate = (raw: string | Date | null | undefined): string => {
  if (!raw) return "—";
  const d = dayjs(raw);
  return d.isValid() ? d.format("DD/MM/YYYY") : "—";
};

/**
 * Everything about one lead that isn't worth a column.
 *
 * The CRM table carries the eight fields you scan a pipeline by; the rest — how they heard,
 * who referred them, the follow-up history, the call note — are things you read once you've
 * picked a lead, so they live here. That's what keeps the table readable rather than a wall
 * of cramped cells.
 *
 * The staff note is editable here rather than in its own modal: opening a lead and writing
 * on it is one action, and saving is what stamps you as the lead's handler.
 */
const LeadDetailPanel: React.FC<LeadDetailPanelProps> = ({
  row,
  noteDraft,
  onNoteDraftChange,
  onSaveNote,
  savingNote,
  onEditFollowup,
  onGoToProfile,
  onClose,
  openCallTaskId,
  onRecordOutcome,
  pendingOutcome,
}) => {
  const tokens = useTokens();
  // The per-call note is deliberately separate from the staff note above: one records what
  // happened on this attempt, the other is the standing note on the lead.
  const [outcomeNote, setOutcomeNote] = React.useState("");
  // FORT-366: every logged call has to schedule the next one, so this is required rather
  // than pre-filled — a default date would be silently accepted on the one-click path and
  // the pipeline would fill with follow-ups nobody actually chose.
  const [nextFollowUp, setNextFollowUp] = React.useState<dayjs.Dayjs | null>(null);
  const membershipId = row?.member?.membershipId;
  // Cleared when the panel moves to a different lead, or the previous lead's attempt note
  // and follow-up date would be sitting in the box ready to be filed against this one.
  React.useEffect(() => {
    setOutcomeNote("");
    setNextFollowUp(null);
  }, [membershipId]);
  // Same source as the retention report, so the CRM and the report never disagree on the wording.
  const { signup } = useClientConfig();
  const howTheyHeardOptions = signup?.howTheyHeard ?? DEFAULT_SIGNUP_HOW_THEY_HEARD;
  const member = row?.member;
  const dueDate = row ? nextDueDate(row) : null;
  const assignmentNote = row?.assignmentNote;
  // Normalised before they reach a URI scheme: both are tenant-entered free text.
  const email = member?.emailAddress?.trim();
  const phone = member?.phoneNumber?.trim();
  const tel = telHref(phone);

  const field = (label: string, value: React.ReactNode, testId?: string) => (
    <Stack gap={0} key={label}>
      <Text variant="caption" color="secondary">
        {label}
      </Text>
      <div data-testid={testId}>{value}</div>
    </Stack>
  );

  return (
    <Drawer
      open={row != null}
      onClose={onClose}
      title={member?.name ?? "Lead"}
      width={420}
      data-testid="lead-detail-panel"
    >
      {member ? (
        <Stack gap={4}>
          <Stack direction="horizontal" gap={1} wrap>
            {leadTypeLabel(member) ? <Tag tone="primary">{leadTypeLabel(member)}</Tag> : null}
            {leadSourceLabel(member) ? <Tag tone="default">{leadSourceLabel(member)}</Tag> : null}
          </Stack>

          <Stack gap={3}>
            {field(
              "Email",
              email ? (
                // The panel is where you act on a lead, so the address is a live mailto
                // rather than text to copy out by hand.
                <a href={`mailto:${encodeURIComponent(email)}`}>{email}</a>
              ) : (
                "—"
              ),
              "lead-detail-email"
            )}
            {field(
              "Phone",
              // Shown as the club typed it; only the href is normalised.
              tel ? <a href={tel}>{phone}</a> : phone || "—",
              "lead-detail-phone"
            )}
            {field("Interests", interestsLabel(member) || "—", "lead-detail-interests")}
            {field(
              "How they heard",
              howTheyHeardLabel(member, howTheyHeardOptions) || "—",
              "lead-detail-how-heard"
            )}
            {field("Referring member", String(member.metadata?.ReferringMember ?? "") || "—")}
            {field("Handled by", member.managedByStaffName ?? "—", "lead-detail-managed-by")}
          </Stack>

          <div
            style={{
              borderTop: `1px solid ${tokens.color.border.default}`,
              paddingTop: tokens.space[3],
            }}
          >
            <Stack gap={3}>
              <Text weight="semibold">Follow-up</Text>
              {field("Last contacted", formatDate(row?.primaryFollowup?.followUpDate), "lead-detail-last-contacted")}
              {field("Last spoke to", row?.primaryFollowup?.contactedByStaffName || "—")}
              {field("Next follow-up", formatDate(dueDate), "lead-detail-next-followup")}
              {field("Follow-up notes", row?.primaryFollowup?.notes || "—", "lead-detail-followup-notes")}
              {assignmentNote
                ? field("Call note", <Tag tone="warning">{assignmentNote}</Tag>, "lead-detail-call-note")
                : null}
              <Button data-testid="lead-detail-edit-followup" onClick={onEditFollowup}>
                Edit follow-up
              </Button>
            </Stack>
          </div>

          {/*
            FORT-361: logging a call outcome lives here now. It used to be the only way to do
            it was a list on Staff Daily, which the client had removed as duplication — so
            without this the buttons would have had no home at all. Shown only when I hold an
            open call on this lead: an outcome against a call nobody owes is not a thing.
          */}
          {openCallTaskId != null && onRecordOutcome ? (
            <div
              style={{
                borderTop: `1px solid ${tokens.color.border.default}`,
                paddingTop: tokens.space[3],
              }}
              data-testid="lead-detail-call-outcome"
            >
              <Stack gap={2}>
                <Text weight="semibold">Log this call</Text>
                <Input
                  value={outcomeNote}
                  onChange={(e) => setOutcomeNote(e.target.value)}
                  placeholder="Note (optional)"
                  data-testid="lead-detail-outcome-note"
                />
                {/*
                  FORT-366: the next follow-up is captured with the outcome, not after it.
                  Logging the call is what completes the task and drops the lead off the
                  day's list, so a date asked for afterwards is a date nobody comes back
                  to give.
                */}
                {/*
                  The testid sits on a wrapper rather than on <DatePicker>: AntD decides
                  for itself where a forwarded data-* prop lands, so anchoring to a div we
                  own keeps the selector stable if that ever changes.

                  Labelled "Call again on", not "Next follow-up" — the read-only display of
                  the stored next follow-up is already on screen above under that name, and
                  it only refreshes after the save, so two identical labels would show the
                  new date next to the stale one.
                */}
                <div data-testid="lead-detail-outcome-next-followup">
                  <DatePicker
                    label="Call again on"
                    value={nextFollowUp}
                    onChange={(value) => setNextFollowUp(value)}
                    format="DD/MM/YYYY"
                    placeholder="Required — when to call next"
                    // A next call in the past would leave the lead permanently overdue:
                    // nextDueDate() keys the CRM's ordering and overdue colouring off
                    // exactly this column.
                    disabledDate={(d) => d.isBefore(dayjs(), "day")}
                  />
                </div>
                {/*
                  Only the pressed button spins. Three identical spinners would leave the
                  user unable to tell which outcome they just filed — and the outcome is
                  written into the follow-up note, so a wrong guess is a wrong record.
                */}
                <Stack direction="horizontal" gap={2} wrap>
                  {OUTCOME_BUTTONS.map(({ outcome, label, testId }) => (
                    <Button
                      key={outcome}
                      loading={pendingOutcome === outcome}
                      // Disabled until a date is picked: the API rejects a dateless outcome,
                      // so letting the click through would just surface a 400 the user
                      // can't act on from here.
                      disabled={
                        nextFollowUp == null || (pendingOutcome != null && pendingOutcome !== outcome)
                      }
                      onClick={() => {
                        // Guard as well as `disabled` — the disabled prop is the UX, this
                        // is what narrows the type and stops a stray call going out dateless.
                        const scheduled = toDateOnly(nextFollowUp);
                        if (scheduled == null) return;
                        onRecordOutcome(openCallTaskId, outcome, outcomeNote, scheduled);
                      }}
                      data-testid={testId}
                    >
                      {label}
                    </Button>
                  ))}
                </Stack>
                {nextFollowUp == null ? (
                  // Danger rather than secondary: this explains why three buttons are dead,
                  // and a muted grey line under them is exactly the kind of gate people
                  // don't see before giving up.
                  <Text variant="caption" color="danger" data-testid="lead-detail-outcome-hint">
                    Set a next follow-up date to log this call.
                  </Text>
                ) : null}
              </Stack>
            </div>
          ) : null}

          <div
            style={{
              borderTop: `1px solid ${tokens.color.border.default}`,
              paddingTop: tokens.space[3],
            }}
          >
            <Stack gap={2}>
              <Text weight="semibold">Staff note</Text>
              <Input
                multiline
                rows={4}
                value={noteDraft}
                onChange={(e) => onNoteDraftChange(e.target.value)}
                placeholder="What happened when you spoke to them?"
                data-testid="lead-detail-note-input"
              />
              <Button
                variant="primary"
                loading={savingNote}
                onClick={onSaveNote}
                data-testid="lead-detail-note-save"
              >
                Save note
              </Button>
            </Stack>
          </div>

          <Button data-testid="lead-detail-go-to-profile" onClick={onGoToProfile}>
            Open member profile
          </Button>
        </Stack>
      ) : null}
    </Drawer>
  );
};

export default LeadDetailPanel;
